import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, BookOpen } from 'lucide-react';
import PublicNav from '@/components/PublicNav';
import { apiCall } from '@/App';
import { Breadcrumbs, breadcrumbJsonLd } from '@/components/blog/Breadcrumbs';
import NotFoundPage from '@/pages/NotFoundPage';
import { staticTopic } from '@/lib/blogStatic';

function StageSection({ stage }) {
  return (
    <section className="mb-8" data-testid={`topic-stage-${stage.intent}`}>
      <h2 className="text-base md:text-lg font-bold text-[#0F172A] mb-3">{stage.label}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {stage.posts.map(p => (
          <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`topic-post-${p.slug}`}
            className="group bg-white border border-[#E9D5FF] rounded-xl p-4 hover:border-[#9333EA] transition-colors">
            <p className="text-sm font-semibold text-[#0F172A] group-hover:text-[#9333EA] leading-snug mb-1">{p.title}</p>
            <p className="text-xs text-[#9CA3AF] line-clamp-2">{p.excerpt || p.meta_description}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function TopicHubPage() {
  const { cluster } = useParams();
  // Render pertama dari data statik (tanpa tunggu API); API menggantikan bila tiba
  const [data, setData] = useState(() => staticTopic(cluster));
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let alive = true;
    const s = staticTopic(cluster);
    setData(s); setMissing(false);
    apiCall('get', `/blog/topics/${cluster}`).then(d => alive && setData(d)).catch(() => alive && !s && setMissing(true));
    return () => { alive = false; };
  }, [cluster]);

  if (missing) return <NotFoundPage />;
  if (!data) return <div className="min-h-screen bg-[#FCFAFF] flex items-center justify-center"><p className="text-[#9CA3AF]">Loading...</p></div>;

  const { topic, stages, total, others } = data;
  const crumbs = [{ label: 'Home', to: '/' }, { label: 'Blog', to: '/blog' }, { label: topic.name, path: `/blog/topik/${topic.slug}` }];
  const desc = topic.intro[0].slice(0, 155);

  return (
    <div className="min-h-screen bg-[#FCFAFF]">
      <Helmet>
        <title>{`${topic.name}: ${total} Panduan | Flexa Blog`}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={`https://flexaviral.com/blog/topik/${topic.slug}`} />
        <meta property="og:title" content={`${topic.name} | Flexa Blog`} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={`https://flexaviral.com/blog/topik/${topic.slug}`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd(crumbs))}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'CollectionPage', name: topic.name, description: desc,
          url: `https://flexaviral.com/blog/topik/${topic.slug}`, inLanguage: 'ms-MY',
        })}</script>
      </Helmet>
      <PublicNav />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Breadcrumbs items={crumbs} />
        <div className="flex items-center gap-2 text-xs font-semibold text-[#9333EA] mb-2"><BookOpen className="h-4 w-4" /> Topik Blog</div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4" data-testid="topic-title">{topic.name}</h1>
        {topic.intro.map((p, i) => <p key={i} className="text-sm text-[#6B7280] leading-relaxed mb-3">{p}</p>)}

        <div className="mt-8">{stages.map(s => <StageSection key={s.intent} stage={s} />)}</div>

        <div className="bg-white border border-[#E9D5FF] rounded-xl p-5 mb-8" data-testid="topic-services">
          <p className="text-sm font-bold text-[#0F172A] mb-2">Servis berkaitan</p>
          <div className="flex flex-wrap gap-2">
            {topic.services.map(([to, label]) => (
              <Link key={to} to={to} className="text-xs font-semibold text-[#9333EA] bg-[#F8F5FF] border border-[#E9D5FF] px-3 py-1.5 rounded-full hover:border-[#9333EA] inline-flex items-center gap-1">{label} <ArrowRight className="h-3 w-3" /></Link>
            ))}
          </div>
        </div>

        <p className="text-sm font-bold text-[#0F172A] mb-2">Topik lain</p>
        <div className="flex flex-wrap gap-2" data-testid="topic-others">
          {others.map(o => <Link key={o.slug} to={`/blog/topik/${o.slug}`} className="text-xs text-[#6B7280] bg-white border border-zinc-200 px-3 py-1.5 rounded-full hover:border-[#9333EA] hover:text-[#9333EA]">{o.name}</Link>)}
        </div>
      </div>
    </div>
  );
}
